import { useMemo } from 'react';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AddDependentModal } from '@app/components';
import { paths } from '@app/constants';
import { getInitials, showDefaultErrorToast, useToggleState } from '@app/utils';
import { useLinkedAccounts, useSwitchAccount, useTokenUserData } from '@data/hooks';
import { useOptimisticSignUpStore } from '@data/storage';
import { AntdAvatar, AntdDropdown, AntdSpace, AntdTooltip, AntdTypography } from '@ui/components';
import { useTheme } from '@ui/hooks';
import { NavBarDropdownTrigger } from '../NavBarDropdownTrigger';

const { Text } = AntdTypography;

export const AccountSwitcher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { textColor } = useTheme();
  const [isAddModalOpen, openAddModal, setAddModalOpen] = useToggleState(false);

  const { firstName, lastName, userId, parentAccountId } = useTokenUserData();
  const { isLoading: isOptimisticSignUpLoading } = useOptimisticSignUpStore();
  const { data } = useLinkedAccounts(!isOptimisticSignUpLoading);
  const { mutate: switchAccount, isPending } = useSwitchAccount();

  const linkedAccounts = data?.linkedAccounts ?? [];
  const isDependent = !!parentAccountId;

  const handleSwitch = (targetUserId: string) => {
    switchAccount(
      { targetUserId },
      {
        onError: showDefaultErrorToast,
      },
    );
  };

  const handleAddSuccess = () => {
    setAddModalOpen(false);
    navigate(`/${paths.app}/${paths.chat}`, { replace: true });
  };

  const menuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: t('common.profile'),
        onClick: () => navigate(`/${paths.app}/${paths.profile.root}`),
      },
      { type: 'divider' as const },
      ...linkedAccounts.map((account) => ({
        key: account.userId,
        label: (
          <AntdSpace>
            <AntdAvatar size="small" style={{ backgroundColor: textColor }}>
              {getInitials(account.firstName, account.lastName)}
            </AntdAvatar>
            <Text>
              {account.firstName} {account.lastName}
              {account.userId === userId && ` (${t('account_select.you')})`}
            </Text>
          </AntdSpace>
        ),
        onClick: account.userId === userId ? undefined : () => handleSwitch(account.userId),
      })),
      { type: 'divider' as const },
      {
        key: 'add',
        icon: <PlusOutlined />,
        disabled: isDependent,
        label: (
          <AntdTooltip
            title={isDependent ? t('account_select.add_profile_disabled_tooltip') : undefined}
          >
            <span>{t('account_select.add_profile')}</span>
          </AntdTooltip>
        ),
        onClick: isDependent ? undefined : openAddModal,
      },
    ],
    [linkedAccounts, userId, textColor, isDependent, t, navigate, openAddModal],
  );

  return (
    <>
      <AntdDropdown
        menu={{ items: menuItems, selectedKeys: [userId] }}
        trigger={['click']}
        disabled={isPending}
        classNames={{ root: 'min-w-[180px] max-w-xs' }}
      >
        <NavBarDropdownTrigger icon={UserOutlined} label={`${firstName} ${lastName}`} />
      </AntdDropdown>

      <AddDependentModal
        open={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddSuccess}
      />
    </>
  );
};
